import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { resolveConnectionErrorMessage } from '../constants/connection-error-messages';

export interface SyncJob {
    job_id: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    source_name?: string;
    /** The UserConnection._id that triggered this sync (set client-side, not from API). */
    connection_id?: string;
    current_step?: string;
    synced_count: number;
    skipped_count: number;
    total_processed: number;
    errors: string[];
    error_message?: string;
    started_at?: string;
    completed_at?: string;
}

/**
 * Narrow an untrusted API status string into the SyncJob status union.
 * Resync/start "start response" payloads type status as a plain string, so a
 * TS cast can't be trusted — an unknown truthy value would slip past `|| 'pending'`
 * and be treated as inactive by `activeJobs`. Default unknown values to 'pending'.
 */
function normalizeStatus(s: unknown): SyncJob['status'] {
    return s === 'pending' || s === 'in_progress' || s === 'completed' || s === 'failed'
        ? s
        : 'pending';
}

interface SyncJobStartResponse {
    messagePath: string;
    data: { job_id: string; status: string; message: string };
}

interface SyncJobStatusResponse {
    messagePath: string;
    data: SyncJob;
}

interface ActiveJobsResponse {
    messagePath: string;
    data: SyncJob[];
}

interface ResyncResponse {
    messagePath: string;
    data: {
        jobs: { job_id: string; status: string; connection_id: string; source_name?: string; message: string }[];
        message: string;
    };
}

@Injectable({ providedIn: 'root' })
export class SyncStatusService implements OnDestroy {
    private readonly http = inject(HttpClient);
    private readonly toast = inject(ToastrService);
    private readonly baseUrl = environment.apiBaseUrl;

    // Active sync jobs keyed by job_id
    readonly jobs = signal<Map<string, SyncJob>>(new Map());

    // Recently completed/failed jobs (for showing ✓/✗ before auto-dismiss)
    readonly recentJobs = signal<SyncJob[]>([]);

    // Fires whenever a sync reaches a terminal state (completed/failed). Consumers
    // (e.g. the Integrations page) reload their connection list so the persisted
    // last_sync_status — which drives the "Sync failed · Resync" card — is refreshed.
    readonly jobTerminated$ = new Subject<{ status: 'completed' | 'failed'; connectionId?: string }>();

    // True while the POST /transactions/resync request is in flight (button is disabled).
    readonly resyncing = signal(false);

   
    private resyncBatch: { pending: Set<string>; total: number; synced: number; failed: number } | null = null;

    // Derived signals
    readonly activeJobs = computed(() => {
        const jobMap = this.jobs();
        return [...jobMap.values()].filter(j => j.status === 'pending' || j.status === 'in_progress');
    });

    readonly hasActiveJobs = computed(() => this.activeJobs().length > 0);
    readonly hasAnyVisibleJobs = computed(() => this.activeJobs().length > 0 || this.recentJobs().length > 0);

    /** Set of connection IDs currently being actively synced. */
    readonly syncingConnectionIds = computed(() =>
        new Set(
            this.activeJobs()
                .map(j => j.connection_id)
                .filter((id): id is string => Boolean(id))
        )
    );

    /** Set of connection IDs whose sync recently completed (visible for ~8 s). */
    readonly recentlySyncedConnectionIds = computed(() =>
        new Set(
            this.recentJobs()
                .filter(j => j.status === 'completed' && j.connection_id)
                .map(j => j.connection_id!)
        )
    );

    private pollIntervals = new Map<string, ReturnType<typeof setInterval>>();
    private pollTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
    private initialized = false;

    /**
     * Initialize — check for active sync jobs on first load.
     * Safe to call multiple times; only runs once.
     */
    init(): void {
        if (this.initialized) return;
        this.initialized = true;
        this.checkForActiveJobs();
    }

    /**
     * Start a new sync: calls POST /sync, receives job_id, begins polling.
     */
    startSync(connectionId: string, connectionName: string): void {
        this.http.post<SyncJobStartResponse>(`${this.baseUrl}/transactions/sync`, {
            connection_id: connectionId,
        }).subscribe({
            next: (res) => {
                const jobId = res?.data?.job_id;
                if (!jobId) return;

                // Add to active jobs immediately
                const job: SyncJob = {
                    job_id: jobId,
                    status: 'pending',
                    source_name: connectionName,
                    connection_id: connectionId,
                    current_step: 'Starting sync...',
                    synced_count: 0,
                    skipped_count: 0,
                    total_processed: 0,
                    errors: [],
                };

                this.jobs.update(m => {
                    const copy = new Map(m);
                    copy.set(jobId, job);
                    return copy;
                });

                this.startPolling(jobId);
            },
            error: (err) => {
                console.error('Failed to start sync', err);
                this.toast.error(
                    resolveConnectionErrorMessage(
                        err,
                        `Couldn't start syncing ${connectionName}. Please try again.`,
                    ),
                    'Sync failed',
                );
            },
        });
    }

    /**
     * Resync a SINGLE connection incrementally.
     * Calls POST /transactions/resync/:connectionId — the backend resumes from the newest
     * stored transaction, so only new activity is fetched (far cheaper than a full /sync).
     * Registers the spawned job and begins polling so the existing sync-status UI tracks it.
     * Use this for a healthy connection; for a *failed* connection prefer startSync (full
     * re-fetch), since a failed sync may have left older history missing that an incremental
     * resume would not backfill.
     */
    resyncConnection(connectionId: string, connectionName: string): void {
        // Guard against double-triggering while this connection is already syncing.
        if (this.syncingConnectionIds().has(connectionId)) return;

        this.http.post<SyncJobStartResponse>(
            `${this.baseUrl}/transactions/resync/${connectionId}`,
            {},
        ).subscribe({
            next: (res) => {
                const jobId = res?.data?.job_id;
                if (!jobId) return;

                const job: SyncJob = {
                    job_id: jobId,
                    status: normalizeStatus(res?.data?.status),
                    source_name: connectionName,
                    connection_id: connectionId,
                    current_step: 'Starting sync...',
                    synced_count: 0,
                    skipped_count: 0,
                    total_processed: 0,
                    errors: [],
                };

                this.jobs.update(m => {
                    const copy = new Map(m);
                    copy.set(jobId, job);
                    return copy;
                });

                this.startPolling(jobId);
            },
            error: (err) => {
                console.error('Failed to resync connection', err);
                this.toast.error(
                    resolveConnectionErrorMessage(
                        err,
                        `Couldn't resync ${connectionName}. Please try again.`,
                    ),
                    'Resync failed',
                );
            },
        });
    }

    /**
     * Resync ALL of the user's active connections in one action.
     * Calls POST /transactions/resync, then registers every spawned job and begins polling
     * so the existing sync-status UI tracks them exactly like an individual sync.
     */
    resyncAll(): void {
        if (this.resyncing()) return;
        this.resyncing.set(true);

        this.http.post<ResyncResponse>(`${this.baseUrl}/transactions/resync`, {}).subscribe({
            next: (res) => {
                this.resyncing.set(false);
                const jobs = res?.data?.jobs ?? [];

                // No connections at all → guide the user to the Integrations page.
                if (jobs.length === 0) {
                    this.toast.warning(
                        "You don't have any connections yet. Connect an exchange or wallet on the Integrations page to start syncing.",
                        'Nothing to resync',
                    );
                    return;
                }

                const started = jobs.filter(j => j.job_id);
                if (started.length === 0) {
                    this.toast.error('Could not start the resync. Please try again in a moment.');
                    return;
                }

                this.jobs.update(m => {
                    const copy = new Map(m);
                    for (const j of started) {
                        copy.set(j.job_id, {
                            job_id: j.job_id,
                            status: normalizeStatus(j.status),
                            source_name: j.source_name,
                            connection_id: j.connection_id,
                            current_step: 'Starting sync...',
                            synced_count: 0,
                            skipped_count: 0,
                            total_processed: 0,
                            errors: [],
                        });
                    }
                    return copy;
                });

                this.resyncBatch = {
                    pending: new Set(started.map(j => j.job_id)),
                    total: started.length,
                    synced: 0,
                    failed: 0,
                };

                for (const j of started) {
                    this.startPolling(j.job_id);
                }

                this.toast.info(`Resyncing ${started.length} connection${started.length > 1 ? 's' : ''}…`);
            },
            error: (err) => {
                this.resyncing.set(false);
                console.error('Failed to resync transactions', err);
                this.toast.error('Failed to start the resync. Please try again.');
            },
        });
    }

    /**
     * For a source whose backend sync is a single synchronous request-response (RWA Assets'
     * `/rwa/sync` today) rather than the async job-id + polling pattern the rest of this service
     * assumes — shows the exact same bottom-corner sync-status widget (mounted once, globally,
     * in the shell layout) without a real job to poll. Call this right before firing the
     * request, `completeSyntheticJob` when its response (or error) arrives.
     *
     * `connectionId` is optional — pass it when the caller already knows which
     * `UserConnection._id` this sync belongs to (e.g. resyncing a wallet that's already
     * connected), so `syncingConnectionIds()`/`recentlySyncedConnectionIds()` pick it up and
     * that connection's card gets the same animated ring / "Sync Complete" badge treatment a
     * polled job's connection gets. Omit it when no connection exists yet (a brand-new wallet's
     * first sync) — there's no card to animate anyway; it'll just appear already "Connected"
     * once the sync completes.
     */
    beginSyntheticJob(sourceName: string, connectionId?: string): string {
        const jobId = `synthetic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const job: SyncJob = {
            job_id: jobId,
            status: 'in_progress',
            source_name: sourceName,
            connection_id: connectionId,
            current_step: 'Syncing...',
            synced_count: 0,
            skipped_count: 0,
            total_processed: 0,
            errors: [],
        };
        this.jobs.update(m => {
            const copy = new Map(m);
            copy.set(jobId, job);
            return copy;
        });
        return jobId;
    }

    /**
     * Drops a job started with `beginSyntheticJob` WITHOUT announcing anything.
     *
     * For a sync the user abandoned: it neither completed nor failed, so neither toast is true —
     * "already up to date" over a sync they just cancelled is actively misleading. This mirrors
     * how a polled job behaves when it is cancelled: only `completed` and `failed` are treated as
     * terminal there, so a cancelled one simply stops appearing and says nothing.
     */
    discardSyntheticJob(jobId: string): void {
        this.jobs.update(m => {
            const copy = new Map(m);
            copy.delete(jobId);
            return copy;
        });
    }

    /** Finishes a job started with `beginSyntheticJob` — moves it into `recentJobs` (✓/✗ shown
     * for ~8s, same as a real polled job's terminal state), fires the usual toast. */
    completeSyntheticJob(jobId: string, syncedCount: number, failed = false, errorMessage?: string): void {
        const prev = this.jobs().get(jobId);
        if (!prev) return;

        const finished: SyncJob = {
            ...prev,
            status: failed ? 'failed' : 'completed',
            synced_count: syncedCount,
            error_message: errorMessage,
            completed_at: new Date().toISOString(),
        };

        this.jobs.update(m => {
            const copy = new Map(m);
            copy.delete(jobId);
            return copy;
        });
        this.recentJobs.update(list => [...list, finished]);
        this.notifyTerminalStatus(finished);

        setTimeout(() => {
            this.recentJobs.update(list => list.filter(j => j.job_id !== jobId));
        }, 8000);
    }

    /**
     * Check for active sync jobs on the backend (hard reload recovery).
     */
    private checkForActiveJobs(): void {
        this.http.get<ActiveJobsResponse>(`${this.baseUrl}/transactions/sync/active`).subscribe({
            next: (res) => {
                const activeJobs = res?.data || [];
                if (activeJobs.length === 0) return;

                this.jobs.update(m => {
                    const copy = new Map(m);
                    for (const job of activeJobs) {
                        copy.set(job.job_id, job);
                    }
                    return copy;
                });

                // Resume polling for each active job
                for (const job of activeJobs) {
                    if (!this.pollIntervals.has(job.job_id)) {
                        this.startPolling(job.job_id);
                    }
                }
            },
            error: () => { /* silently fail — user might not be logged in */ },
        });
    }

    /**
     * Poll a job's status every 3 seconds.
     */
    private startPolling(jobId: string): void {
        // Don't double-poll
        if (this.pollIntervals.has(jobId)) return;

        const interval = setInterval(() => {
            this.http.get<SyncJobStatusResponse>(`${this.baseUrl}/transactions/sync/status/${jobId}`).subscribe({
                next: (res) => {
                    const status = res?.data;
                    if (!status) return;

                    // Update job in map — preserve client-side connection_id across API updates
                    this.jobs.update(m => {
                        const copy = new Map(m);
                        const prev = copy.get(jobId);
                        copy.set(jobId, { ...status, connection_id: prev?.connection_id });
                        return copy;
                    });

                    // Handle terminal states
                    if (status.status === 'completed' || status.status === 'failed') {
                        this.stopPolling(jobId);
                        // Notify listeners (read connection id from the map before it's removed).
                        this.jobTerminated$.next({
                            status: status.status,
                            connectionId: this.jobs().get(jobId)?.connection_id,
                        });
                        const inBatch = this.settleResyncJob(
                            jobId,
                            status.status === 'failed',
                            status.synced_count ?? 0,
                        );
                        if (!inBatch) this.notifyTerminalStatus(status);

                        // Move to recent jobs
                        this.recentJobs.update(list => [...list, status]);

                        // Remove from active map
                        this.jobs.update(m => {
                            const copy = new Map(m);
                            copy.delete(jobId);
                            return copy;
                        });

                        // Auto-dismiss recent job after 8 seconds
                        setTimeout(() => {
                            this.recentJobs.update(list => list.filter(j => j.job_id !== jobId));
                        }, 8000);
                    }
                },
                error: () => { /* transient error — keep polling */ },
            });
        }, 3000);

        this.pollIntervals.set(jobId, interval);

        // Safety timeout: 10 minutes
        const timeout = setTimeout(() => {
            this.stopPolling(jobId);
            // Don't let a timed-out job stall the resync batch's aggregate toast.
            this.settleResyncJob(jobId, true, 0);
        }, 10 * 60 * 1000);
        this.pollTimeouts.set(jobId, timeout);
    }

    private settleResyncJob(jobId: string, failed: boolean, syncedCount: number): boolean {
        const batch = this.resyncBatch;
        if (!batch || !batch.pending.has(jobId)) return false;

        batch.pending.delete(jobId);
        if (failed) batch.failed += 1;
        else batch.synced += syncedCount;

        if (batch.pending.size === 0) {
            this.resyncBatch = null;
            this.notifyResyncComplete(batch.total, batch.synced, batch.failed);
        }
        return true;
    }

    private notifyResyncComplete(total: number, synced: number, failed: number): void {
        const conns = `${total} connection${total === 1 ? '' : 's'}`;
        if (failed === 0) {
            this.toast.success(
                synced > 0
                    ? `Resync complete — ${synced} new transaction${synced === 1 ? '' : 's'} across ${conns}.`
                    : `Resync complete — all ${conns} are already up to date.`,
                'Resync successful',
            );
        } else if (failed === total) {
            this.toast.error(`Resync failed for all ${conns}. Please try again.`, 'Resync failed');
        } else {
            this.toast.warning(
                `Resync finished — ${total - failed} of ${conns} synced, ${failed} failed.`,
                'Partial resync',
            );
        }
    }

    private notifyTerminalStatus(status: SyncJob): void {
        const name = status.source_name?.trim() || 'connection';
        if (status.status === 'completed') {
            const synced = status.synced_count ?? 0;
            this.toast.success(
                synced > 0
                    ? `Synced ${synced} transaction${synced === 1 ? '' : 's'} from ${name}.`
                    : `${name} is already up to date.`,
                'Sync complete',
            );
            return;
        }
        const reason = status.error_message?.trim() || status.errors?.[0]?.trim();
        this.toast.error(
            reason ? `Sync failed for ${name}: ${reason}` : `Sync failed for ${name}. Please try again.`,
            'Sync failed',
        );
    }

    private stopPolling(jobId: string): void {
        const interval = this.pollIntervals.get(jobId);
        if (interval) {
            clearInterval(interval);
            this.pollIntervals.delete(jobId);
        }
        const timeout = this.pollTimeouts.get(jobId);
        if (timeout) {
            clearTimeout(timeout);
            this.pollTimeouts.delete(jobId);
        }
    }

    ngOnDestroy(): void {
        for (const [jobId] of this.pollIntervals) {
            this.stopPolling(jobId);
        }
    }
}
